// ============================================================================
// The OG identity — one KEY and one BASE for a development number
// (DEV-04, David 10.10.26 · Convention #188)
// ============================================================================
// OG means the ORIGINAL model, as drawn, with none of our changes. A letter
// (A, B, C …) is a variant and never the original. A number with no letter does
// not prove an original — only an explicit OG does. So:
//
//   devNoKey  — upper-cased, whitespace collapsed, trimmed, and an OG suffix in
//               any spelling ("(OG)", " OG", "-OG", "_OG", ".OG", "( og )", or
//               OG straight after a digit) written "<BASE> OG" with one space.
//               "DA3355 (OG)" → "DA3355 OG" · "ds3095-og" → "DS3095 OG".
//   devNoBase — the key without its trailing " OG". "DS3095 OG" → "DS3095";
//               "DA3355A" stays "DA3355A" — a letter is never stripped.
//
// A development and an order identifier are the SAME model when their bases are
// equal — unless the base is held by two active developments (an "X" and an
// "X OG" both registered), when only the exact number matches. The server
// applies that rule (sql/2026-10-09-og-identity-v1.sql); the client reads the
// key only for the PRE-ORDER badge (orderIdentity.deriveOrderStatus).
//
// THE KEY IS FOR MATCHING ONLY. It is never written back over a stored
// dev_number or item_no.
//
// THE TWIN: public._dev_no_key / public._dev_no_base, the same three patterns in
// the same order (the SQL chains them in one expression; once a pattern has
// written "<BASE> OG" the later ones leave it alone, so the chain and this
// first-match answer the same). ONE fixture proves the two agree —
// tests/fixtures/orders/dev-no-key.json, read by devNoKey.test.js and by
// scripts/proofs/og-identity-sql-proof.mjs. Change one side, change both.
// Parity is over ASCII: whitespace is the six ASCII space characters on both
// sides (never a locale's idea of a space), and development numbers are ASCII by
// the create form's own rule.
// ============================================================================

const WS = /[ \t\n\r\f\v]+/g;
const OG_PAREN = /^(.*[0-9A-Z])[ ._-]*\( ?OG ?\)$/;   // "DA3355 (OG)", "DA3355(OG)", "DA3355 ( OG )"
const OG_SEP = /^(.*[0-9A-Z])[ ._-]+OG$/;             // "DA3355 OG", "DA3355-OG", "DA3355_OG", "DA3355.OG"
const OG_DIGIT = /^(.*[0-9])OG$/;                     // "DS2956OG" — only straight after a digit

/** A development code read on its KEY: DA/DS/DO + 3–5 digits + an optional
 *  letter + an optional " OG". The server's harvest pattern, byte for byte. */
export const DEV_KEY_PATTERN = /^D[ASO][0-9]{3,5}[A-Z]?( OG)?$/;

/** The matching key, or null for an empty value. Pure. */
export function devNoKey(value) {
  if (value == null) return null;
  const t = String(value).toUpperCase().replace(WS, ' ').replace(/^ +| +$/g, '');
  if (t === '') return null;
  const m = OG_PAREN.exec(t) || OG_SEP.exec(t) || OG_DIGIT.exec(t);
  return m ? `${m[1]} OG` : t;
}

/** The key without its trailing " OG", or null for an empty value. Pure. */
export function devNoBase(value) {
  const k = devNoKey(value);
  return k == null ? null : k.replace(/ OG$/, '');
}
